import {
  HiOutlineClipboardCheck,
  HiOutlineClipboardList,
  HiOutlineCog,
  HiOutlineDocumentText,
  HiOutlineSearch,
  HiOutlineShieldExclamation,
  HiOutlineSparkles,
} from "react-icons/hi";

const ICONS = [
  HiOutlineClipboardList,
  HiOutlineSearch,
  HiOutlineClipboardCheck,
  HiOutlineCog,
  HiOutlineShieldExclamation,
  HiOutlineDocumentText,
];

const DOCUMENT_STEP_LABELS = [
  "Document Intake",
  "Data Extraction",
  "Rule Validation",
  "RPA Posting",
  "Exception Review",
  "Audit & Close",
];

const WORKFLOW_STEP_LABELS = [
  "Trigger Intake",
  "Data Collection",
  "Rule Check",
  "Workflow Route",
  "Human Review",
  "System Update",
];

const PROCESS_KEYWORDS = [
  { match: /invoice|billing|receipt/i, doc: ["Invoice Intake", "OCR Extraction", "PO/GRN Match", "Coding & Post", "Exception Review", "Audit Trail"], wf: ["Invoice Trigger", "Data Gather", "Policy Check", "Approval Route", "Reviewer Sign-off", "ERP Update"] },
  { match: /payment|payable|disbursement/i, doc: ["Payment Doc Intake", "Field Extraction", "Amount Validation", "Payment Posting", "Exception Queue", "Audit Log"], wf: ["Payment Batch", "Validation Rules", "Approval Matrix", "Route Approvers", "Treasury Review", "Bank File Export"] },
  { match: /reconcil|statement|matching/i, doc: ["Statement Intake", "Line Extraction", "Match Rules", "Recon Posting", "Mismatch Review", "Close & Archive"], wf: ["Recon Trigger", "Ledger Pull", "Auto-Match", "Exception Route", "Analyst Review", "Recon Report"] },
  { match: /approval|authorize/i, doc: ["Request Intake", "Doc Extraction", "Policy Validation", "Approval Post", "Escalation Review", "Status Update"], wf: ["Request Intake", "Policy Check", "Approver Lookup", "Notification Route", "Manager Approval", "System Update"] },
  { match: /onboard|hire|recruit/i, doc: ["Form Intake", "ID Extraction", "Compliance Check", "HRMS Entry", "Exception Review", "Onboarding Pack"], wf: ["Case Open", "Data Collection", "Eligibility Check", "Task Routing", "HR Review", "HRMS Update"] },
  { match: /claim|policy|insurance/i, doc: ["Claim Intake", "Doc Extraction", "Coverage Check", "Claims Post", "Adjuster Review", "Claim Close"], wf: ["Claim Trigger", "Policy Lookup", "Fraud Rules", "Route Adjuster", "Supervisor Review", "Claims Update"] },
  { match: /order|sales|quote/i, doc: ["Order Intake", "Line Extraction", "Price Validation", "Order Entry", "Exception Review", "Confirm & Log"], wf: ["Order Trigger", "Customer Data", "Credit Check", "Approval Route", "Ops Review", "OMS Update"] },
  { match: /inventory|stock|warehouse/i, doc: ["Doc Intake", "SKU Extraction", "Stock Validation", "WMS Update", "Variance Review", "Inventory Log"], wf: ["Stock Trigger", "System Sync", "Reorder Rules", "Alert Route", "Planner Review", "WMS Update"] },
  { match: /report|compliance|audit/i, doc: ["Report Intake", "Data Extraction", "Compliance Rules", "Report Filing", "Auditor Review", "Evidence Archive"], wf: ["Report Schedule", "Data Pull", "Control Check", "Review Route", "Auditor Sign-off", "Report Publish"] },
];

function parseList(value, fallback = []) {
  if (!value) return fallback;
  return value
    .split(/[,;]/)
    .map((s) => s.trim())
    .filter(Boolean)
    .slice(0, 6);
}

function parseAgents(agentsRequired) {
  if (!agentsRequired) return [];
  return agentsRequired
    .split(/[,;]/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function deriveAgentRole(agentName = "") {
  const lower = agentName.toLowerCase();
  if (lower.includes("intake") || lower.includes("capture")) return "Capture & Classify";
  if (lower.includes("extract") || lower.includes("ocr") || lower.includes("document")) return "Extract & Parse";
  if (lower.includes("valid") || lower.includes("rule") || lower.includes("match")) return "Validate & Check";
  if (lower.includes("rout") || lower.includes("workflow") || lower.includes("action")) return "Route & Execute";
  if (lower.includes("exception") || lower.includes("escalat")) return "Review & Approve";
  if (lower.includes("post") || lower.includes("bot") || lower.includes("runner")) return "Post & Update";
  return "Automate & Execute";
}

function solutionTasks(solution) {
  if (!solution) return [];
  const action = solution.replace(/^Deploy (a|an) [^.]+ solution to /i, "");
  const parts = action
    .replace(/\.\s*The workflow.*/i, "")
    .split(/\s+and\s+|,\s+/)
    .map((p) => p.trim())
    .filter((p) => p.length > 8);
  return parts.slice(0, 6);
}

function stepLabelsForUseCase(useCase, isDocumentRpa) {
  const text = `${useCase.businessProcess} ${useCase.id} ${useCase.challenge}`;
  for (const rule of PROCESS_KEYWORDS) {
    if (rule.match.test(text)) {
      return isDocumentRpa ? rule.doc : rule.wf;
    }
  }
  return isDocumentRpa ? DOCUMENT_STEP_LABELS : WORKFLOW_STEP_LABELS;
}

function buildStepTasks(stepIndex, useCase, inputs, outputs, solutionParts, labels) {
  const bp = useCase.businessProcess || useCase.title;
  const tasks = [];

  if (stepIndex === 0 && inputs.length) {
    tasks.push(...inputs.slice(0, 3));
  } else if (stepIndex === 5 && outputs.length) {
    tasks.push(...outputs.slice(0, 3));
  } else if (solutionParts[stepIndex]) {
    tasks.push(solutionParts[stepIndex].charAt(0).toUpperCase() + solutionParts[stepIndex].slice(1));
  }

  const fillers = [
    [`Receive ${bp} inputs`, "Apply business rules", "Create process case"],
    ["Pull source records", "Enrich from systems", "Prepare validation set"],
    ["Run policy checks", "Score exceptions", "Match master data"],
    ["Route to next step", "Update case status", "Notify stakeholders"],
    ["Review flagged items", "Capture approval", "Track SLA"],
    ["Post to target system", "Archive evidence", "Publish status"],
  ];

  for (const line of fillers[stepIndex] || []) {
    if (tasks.length >= 3) break;
    if (!tasks.includes(line)) tasks.push(line);
  }

  return tasks.slice(0, 3);
}

function reviewerLabel(useCase) {
  const dept = useCase.department || useCase.subIndustry || "Operations";
  if (/finance|payable|receivable|treasury|tax|ledger/i.test(dept)) return "Finance Reviewer";
  if (/hr|payroll|recruit/i.test(dept)) return "HR Reviewer";
  if (/health|clinical|patient/i.test(dept)) return "Clinical Reviewer";
  if (/logistics|freight|warehouse/i.test(dept)) return "Ops Reviewer";
  return `${dept} Review`;
}

function flowDiagramTitle(useCase) {
  return useCase.businessProcess || useCase.shortTitle || useCase.title;
}

function flowDiagramSubtitle(businessProcess, isDocumentRpa) {
  if (isDocumentRpa) {
    return `Document intake, validation, and system update for ${businessProcess} with reviewer checkpoints.`;
  }
  return `Orchestration, approvals, and system update for ${businessProcess} with reviewer checkpoints.`;
}

export function buildRpaFlowConfig(useCase) {
  const isDocumentRpa = (useCase.automationType || "").includes("Document AI");
  const bp = useCase.businessProcess || useCase.title;
  const agents = parseAgents(useCase.agentsRequired);
  const inputs = parseList(useCase.inputData, [
    `${bp} source documents`,
    "Business rules & SOPs",
    "Master data references",
  ]);
  const outputs = parseList(useCase.outputActions, [
    "Validated record",
    "System update",
    "Exception report",
    "Audit log entry",
  ]);
  const solutionParts = solutionTasks(useCase.solution);
  const labels = stepLabelsForUseCase(useCase, isDocumentRpa);
  const subtitle = flowDiagramSubtitle(bp, isDocumentRpa);

  const steps = labels.map((label, index) => ({
    id: index + 1,
    title: label,
    icon: ICONS[index] || HiOutlineCog,
    tasks: buildStepTasks(index, useCase, inputs, outputs, solutionParts, labels),
    agent: agents[index] || (isDocumentRpa ? "RPA Bot" : "Workflow Bot"),
    agentRole: deriveAgentRole(agents[index]),
  }));

  return {
    title: flowDiagramTitle(useCase),
    subtitle,
    inputs: inputs.slice(0, 5),
    outputs: outputs.slice(0, 5),
    checkpoints: [{ label: reviewerLabel(useCase), startCol: 4 }],
    systems: parseList(useCase.systems, ["ERP", "Email", "Document Management"]),
    steps,
  };
}

